import { type TransitionEvent, type UIEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { PanelLeftIcon, Star, StarOutlineOutlined } from 'components/icons';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { Drawer, DrawerContent } from 'components/ui/drawer';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReducedMotion } from 'hooks/useReducedMotion';
import AdminNavigation from 'pages/admin/AdminNavigation';
import { isAdminPath } from 'pages/admin/adminItems';
import { useDispatch, useSelector } from 'store';
import {
    useGetCurrentTenantIconQuery,
    useGetCurrentTenantLogoQuery,
    useGetCurrentTenantQuery,
} from 'store/api/tenants';
import { openDrawer } from 'store/reducers/menu';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { rememberSidebarExpanded } from 'utils/sidebarExpanded';
import { FOCUS_RING } from '../focusRing';
import WelcomePopover from '../WelcomePopover';
import { EnvChip, EnvMarker } from './EnvChip';
import Navigation from './Navigation';
import Pinned from './Pinned';
import PinDragChip from './PinDragChip';
import SidebarFooter from './SidebarFooter';
import SidebarTop from './SidebarTop';
import { usePinDrag, usePinDragState } from './usePinDrag';

const READ_TENANTS = ['READ.TENANTS'];

// Written on the menu itself rather than held in state: the menu remounts around an Admin page, and a fresh one starts
// unscrolled with no fade.
function rampTopFade({ currentTarget: menu }: UIEvent<HTMLElement>) {
    menu.style.setProperty('--menu-top-fade', `${Math.min(menu.scrollTop, 28)}px`);
}

/**
 * The rail's stand-in for Pinned while a record or a view is dragged: a star tile under My work, which the magnet
 * docks the chip onto. A drop on it pins at the end.
 */
function RailPinTile() {
    const { drag } = usePinDragState();
    if (drag?.phase !== 'drag') return null;
    return (
        <div
            data-pin-tile
            aria-hidden
            className={cn(
                'text-changed-ink mt-1.5 flex size-9 origin-top items-center justify-center rounded-md border-[1.5px] transition-[opacity,scale] duration-(--duration-quick) ease-out starting:opacity-0 motion-safe:starting:scale-95',
                drag.docked
                    ? 'border-secondary bg-changed ring-secondary/30 ring-4'
                    : 'border-changed-border bg-changed/60 border-dashed'
            )}
        >
            <span className="t-icon-swap" data-state={drag.docked ? 'b' : 'a'}>
                <span className="t-icon" data-icon="a">
                    <StarOutlineOutlined className="size-4" />
                </span>
                <span className="t-icon" data-icon="b">
                    <Star className="size-4" />
                </span>
            </span>
        </div>
    );
}

type PanelProps = {
    open: boolean;
    onToggle: () => void;
};

// The header with search and Ask AI under it, the navigation (the only part that scrolls) and the
// footer. The header names the tenant, the product until the tenant is read, with the environment chip after it, in
// place of classic's environment ribbon. Its tile is the tenant's square icon when it has one, else the initial; the
// tenant's uploaded logo, when it has one, takes the tile's place. On an Admin page the Admin navigation is the whole
// open sidebar, with neither header nor footer. The rail keeps the tile alone in its header, as a wide logo would not
// read at its size, marked with the environment: a button named for what it does, which opens the sidebar, as the
// classic drawer does.
function SidebarPanel({ open, onToggle }: PanelProps) {
    const { pathname } = useLocation();
    const isAdmin = isAdminPath(pathname);
    const canReadTenant = useHasPermissions(READ_TENANTS);
    const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: !canReadTenant });
    const { data: logoUrl } = useGetCurrentTenantLogoQuery(undefined, { skip: !tenant?.hasCustomLogo });
    const { data: iconUrl } = useGetCurrentTenantIconQuery(undefined, { skip: !tenant?.hasCustomIcon });
    const [failedUrls, setFailedUrls] = useState<string[]>([]);
    // A skipped query still hands back its last URL, so a removed image shows only while the tenant says it is there.
    const shown = (uploaded: boolean | undefined, url: string | undefined) =>
        uploaded && url && !failedUrls.includes(url) ? url : undefined;
    const logo = shown(tenant?.hasCustomLogo, logoUrl);
    const icon = shown(tenant?.hasCustomIcon, iconUrl);
    const markFailed = (url: string) => () => setFailedUrls((urls) => [...urls, url]);
    const name = tenant?.name || t('crespect-logo');
    // A firm's name and initial keep their own casing; only the product name is set in lowercase.
    const brandCase = !tenant?.name && 'lowercase';
    const tile = icon ? (
        <img
            src={icon}
            alt=""
            onError={markFailed(icon)}
            className="bg-paper outline-image-edge size-7 shrink-0 rounded-md object-cover outline -outline-offset-1"
        />
    ) : (
        <span
            aria-hidden
            className={cn(
                'bg-primary text-primary-foreground flex size-7 shrink-0 items-center justify-center rounded-md font-semibold',
                brandCase
            )}
        >
            {name.charAt(0)}
        </span>
    );

    return (
        <div className="bg-sidebar text-sidebar-foreground flex h-full flex-col overflow-x-hidden whitespace-nowrap">
            <div
                className={cn(
                    'flex h-14 shrink-0 items-center',
                    open ? 'gap-2.5 ps-4 pe-3' : 'justify-center',
                    isAdmin && open && 'hidden'
                )}
            >
                {open ? (
                    <>
                        <Link
                            to="/home"
                            className={cn('flex min-w-0 flex-1 items-center gap-2.5 rounded-md', FOCUS_RING)}
                        >
                            {logo ? (
                                <img
                                    src={logo}
                                    alt=""
                                    onError={markFailed(logo)}
                                    className="sidebar-fade bg-paper outline-image-edge h-7 w-auto max-w-24 shrink-0 rounded-md object-contain outline -outline-offset-1"
                                />
                            ) : (
                                tile
                            )}
                            <span className="sidebar-fade flex min-w-0 items-center gap-1.5">
                                <span className={cn('text-section min-w-0 leading-[1.2] font-semibold', brandCase)}>
                                    <EllipsisTooltip>{name}</EllipsisTooltip>
                                </span>
                                <EnvChip />
                            </span>
                        </Link>
                        <button
                            type="button"
                            aria-label={t('collapse')}
                            onClick={onToggle}
                            className={cn(
                                'sidebar-fade hover:bg-sidebar-hover hit-area relative flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-[scale] duration-(--press-dur) ease-out motion-safe:not-aria-[haspopup]:will-change-transform motion-safe:active:not-aria-[haspopup]:scale-(--press-scale)',
                                FOCUS_RING
                            )}
                        >
                            <PanelLeftIcon className="size-[18px]" />
                        </button>
                    </>
                ) : (
                    <EnvMarker label={t('expand-sidebar')}>
                        <button
                            type="button"
                            aria-label={t('expand-sidebar')}
                            aria-expanded={false}
                            onClick={onToggle}
                            className={cn('hit-area relative cursor-pointer rounded-md', FOCUS_RING)}
                        >
                            {tile}
                        </button>
                    </EnvMarker>
                )}
            </div>
            {isAdmin ? (
                <AdminNavigation collapsed={!open} onCollapse={onToggle} />
            ) : (
                <>
                    <SidebarTop collapsed={!open} />
                    {/* No rules around the menu: it fades out at the bottom instead, over its own end padding, and at
                        the top once scrolled, the fade growing with the first 28px of scroll. `scroll-py-7` keeps an
                        entry scrolled into view clear of both fades.
                        `relative`: the menu holds its screen-reader-only labels, which are absolutely positioned. Held
                        by the page, those below the window's bottom edge would make the whole document scroll. */}
                    <div
                        onScroll={rampTopFade}
                        className={cn(
                            'relative min-h-0 flex-1 overflow-y-auto',
                            open &&
                                'scroll-py-7 mask-t-from-[calc(100%-var(--menu-top-fade,0px))] mask-b-from-[calc(100%-28px)]'
                        )}
                    >
                        <Navigation collapsed={!open} pinned={open ? <Pinned /> : <RailPinTile />} />
                    </div>
                </>
            )}
            {!isAdmin && <SidebarFooter collapsed={!open} />}
        </div>
    );
}

/**
 * The new design's sidebar: 256px open and a 48px rail collapsed, following the menu store's
 * `drawerOpen`. Under 768px it is an off-canvas drawer instead, and `drawerOpen` is whether it is out.
 */
function Sidebar() {
    usePinDrag();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const drawerOpen = useSelector((state) => state.menu.drawerOpen);
    const setOpen = (open: boolean) => dispatch(openDrawer({ drawerOpen: open }));
    // The menu store starts from classic's breakpoint and the layout corrects it for this one as it mounts: the panel
    // only slides from the frame after, so that correction never plays as a slide. Until then the panel is
    // `transition-none`: its duration alone would transition every property.
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        const frame = requestAnimationFrame(() => setSettled(true));
        return () => cancelAnimationFrame(frame);
    }, []);
    // The layout the panel draws. Expanding, the open one at once; collapsing, it stays until the panel's slide ends,
    // or at once when there is no slide to wait for. What the rail does not draw fades with the slide meanwhile, on
    // the phase the nav carries (`.sidebar-fade` in `index.css`).
    const reducedMotion = useReducedMotion();
    const [panelOpen, setPanelOpen] = useState(drawerOpen);
    const slides = settled && !isMobile && !reducedMotion;
    if (panelOpen !== drawerOpen && (drawerOpen || !slides)) setPanelOpen(drawerOpen);
    // The off-canvas drawer's open and close are not a choice to keep: only the desktop toggle is remembered.
    const toggle = () => {
        if (!isMobile) rememberSidebarExpanded(!drawerOpen);
        setOpen(!drawerOpen);
    };

    const panel = <SidebarPanel open={panelOpen || isMobile} onToggle={toggle} />;
    // Its fade ends with it, and a transition inside it bubbles up to it: neither is the slide. The property is read
    // off the native event, as React leaves it off a cancel's.
    const endCollapse = (event: TransitionEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && event.nativeEvent.propertyName === 'translate' && !drawerOpen) {
            setPanelOpen(false);
        }
    };

    if (isMobile) {
        return (
            <>
                <Drawer open={drawerOpen} onOpenChange={setOpen} swipeDirection="left">
                    <DrawerContent
                        aria-label={t('navigation')}
                        className="bg-sidebar border-sidebar-border data-[swipe-axis=x]:[--drawer-content-width:16rem]"
                    >
                        {panel}
                    </DrawerContent>
                </Drawer>
                {/* The first visit's Welcome. The New design row, which opens it everywhere else, is in the drawer,
                    and so not in the page until the drawer is out. */}
                <WelcomePopover />
            </>
        );
    }

    // The nav takes its width in one step, so the page beside it lays out once per toggle. The panel makes the motion:
    // always 256px, it slides the 208px between the open width and the rail's, over the page while it collapses, and
    // draws the rail in its last 48px.
    return (
        <nav
            aria-label={t('navigation')}
            data-sidebar-phase={drawerOpen ? 'open' : panelOpen ? 'collapsing' : 'rail'}
            className={cn('relative z-(--z-drawer) shrink-0 select-none', drawerOpen ? 'w-64' : 'w-12')}
        >
            <div
                onTransitionEnd={endCollapse}
                // A theme switch turns every transition off for a frame, which cancels the slide with no end event;
                // a collapse pressed over an expand cancels the expand's slide too, but starts its own.
                onTransitionCancel={(event) => {
                    const slide = event.currentTarget
                        .getAnimations()
                        .some((animation) => (animation as CSSTransition).transitionProperty === 'translate');
                    if (!slide) endCollapse(event);
                }}
                className={cn(
                    'absolute inset-y-0 start-0 w-64',
                    settled ? 'motion-safe:transition-[translate,--sidebar-fade]' : 'transition-none',
                    !panelOpen && 'ps-52',
                    // The rail's edge is a shadow, which forced colours drop: its border is drawn there alone, since
                    // anywhere else it would take a pixel off the rail and show the page behind it.
                    drawerOpen
                        ? 'border-sidebar-border border-e motion-safe:duration-225 motion-safe:ease-out'
                        : 'shadow-card-raised -translate-x-52 motion-safe:duration-195 motion-safe:ease-[cubic-bezier(0.4,0,0.6,1)] rtl:translate-x-52 forced-colors:border-e'
                )}
            >
                {panel}
            </div>
            <PinDragChip />
        </nav>
    );
}

export default Sidebar;
